import { useState } from "react";
import { Plus } from "lucide-react";
import { ActiveFilters, Button, FacetFilter, type Facet, type FacetFilterLabels, type FacetFilterValue } from "@fox1ck-org/ui";

import { useT } from "@/lib/i18n";

/**
 * The card's «+ Фильтр» (доски CConversions, CClicks): the package's
 * `FacetFilter` behind a dashed button and `ActiveFilters` for what is set,
 * over a plain record of facet → values that the tab keeps in its URL. The
 * server filters on ONE value per facet, so a facet keeps the value picked
 * last.
 */
export function CardFacets({ facets, value, onChange }: { facets: Facet[]; value: Record<string, string>; onChange: (patch: Record<string, string | null>) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const record: FacetFilterValue = Object.fromEntries(Object.entries(value).filter(([, v]) => v).map(([k, v]) => [k, [v]]));
  const labels: FacetFilterLabels = {
    trigger: t("card.filter.add"),
    facets: t("card.filter.facets"),
    search: () => t("card.filter.search"),
    apply: t("card.filter.apply"),
    reset: t("card.filter.reset"),
    noOptions: t("card.filter.none"),
    back: t("card.filter.back"),
  };
  return (
    <>
      <ActiveFilters
        value={record}
        facets={facets}
        onRemove={(id) => onChange({ [id]: null })}
        onEdit={(id) => {
          setActive(id);
          setOpen(true);
        }}
        labels={{ remove: (f) => t("card.filter.remove", { facet: f }), edit: (f) => t("card.filter.edit", { facet: f }), list: t("card.filter.active") }}
      />
      <FacetFilter
        facets={facets}
        value={record}
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setActive(null);
        }}
        activeFacetId={active}
        onActiveFacetChange={setActive}
        labels={labels}
        onChange={(next) => {
          // One write for every facet the draft touched (setFilters composes; two setFilter calls do not).
          const patch: Record<string, string | null> = {};
          for (const f of facets) {
            const picked = next[f.id];
            const values = Array.isArray(picked) ? (picked as readonly string[]) : [];
            const last = values[values.length - 1] ?? null;
            if ((value[f.id] || null) !== last) patch[f.id] = last;
          }
          if (Object.keys(patch).length > 0) onChange(patch);
        }}
        trigger={
          <Button variant="outline" size="sm" className="border-dashed">
            <Plus className="size-3.5" /> {t("card.filter.add")}
          </Button>
        }
      />
    </>
  );
}
